import { z } from 'zod';
import {
  TASK_AUDIT_ACTIONS,
  TASK_AUDIT_ENTITY_TYPES,
  TASK_AUDIT_SOURCES,
  TASK_CADENCES,
  TASK_SOURCES,
  TASK_STATUSES,
} from '@/shared/types/taskQueue.types';

const TaskStatusSchema = z.enum(TASK_STATUSES);
const TaskSourceSchema = z.enum(TASK_SOURCES as unknown as [string, ...string[]]);
const TaskCadenceSchema = z.enum(TASK_CADENCES as unknown as [string, ...string[]]);

export const TaskTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  retired: z.boolean(),
});

export const TaskListRowSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  status: TaskStatusSchema,
  assigneeId: z.string().uuid().nullable(),
  assigneeName: z.string().nullable(),
  priority: z.number(),
  source: TaskSourceSchema,
  slackPermalink: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  tagIds: z.array(z.string().uuid()),
  tagNames: z.array(z.string()),
});

export const TaskCommentSchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  authorId: z.string().uuid(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});

export const TaskAuditEntrySchema = z.object({
  id: z.string().uuid(),
  entityType: z.enum(TASK_AUDIT_ENTITY_TYPES as unknown as [string, ...string[]]),
  entityId: z.string().uuid(),
  actorId: z.string().uuid().nullable(),
  actorName: z.string().nullable(),
  action: z.enum(TASK_AUDIT_ACTIONS as unknown as [string, ...string[]]),
  changes: z.record(z.string(), z.unknown()),
  source: z.enum(TASK_AUDIT_SOURCES as unknown as [string, ...string[]]),
  approvalRef: z.string().nullable(),
  createdAt: z.string(),
});

export const TaskDetailSchema = TaskListRowSchema.extend({
  spec: z.string().nullable(),
  recurringDefId: z.string().uuid().nullable(),
  periodKey: z.string().nullable(),
  updatedBy: z.string().uuid(),
  completedAt: z.string().nullable(),
  comments: z.array(TaskCommentSchema),
});

export const RecurringTaskDefSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  spec: z.string().nullable(),
  cadence: TaskCadenceSchema,
  priority: z.number(),
  active: z.boolean(),
  approvedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdBy: z.string().uuid(),
  updatedBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  tagIds: z.array(z.string().uuid()),
  tagNames: z.array(z.string()),
});

export const TaskListResponseSchema = z.object({
  tasks: z.array(TaskListRowSchema),
});

export const TaskDetailResponseSchema = z.object({
  task: TaskDetailSchema,
});

export const TaskHistoryResponseSchema = z.object({
  history: z.array(TaskAuditEntrySchema),
});

export const RecurringDefListResponseSchema = z.object({
  defs: z.array(RecurringTaskDefSchema),
});

export const TaskTagsResponseSchema = z.object({
  tags: z.array(TaskTagSchema),
});

export const TaskAssigneeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export const TaskAssigneesResponseSchema = z.object({
  assignees: z.array(TaskAssigneeSchema),
});

export type TaskListRowDto = z.infer<typeof TaskListRowSchema>;
export type TaskDetailDto = z.infer<typeof TaskDetailSchema>;
